import { Record as record, List } from 'immutable';

import Station from './Station';
import { construct, fields, resolve, resolveAll } from '../helpers/LinkMapper';
import {
    FulfilmentType,
    SmartcardErrorBlocking,
    SmartCardErrorBlockingForLoggedIn,
    SmartcardServiceErrors,
} from '../constants/AppConstants';
import FeatureFlagStore from '../stores/FeatureFlagStore';
import UserStore from '../stores/UserStore';
import config from '../config';
import trainTime from '../lib/TrainTime';

const defaults = {
    id: null,
    fulfilmentMethod: null,
    internalName: null,
    displayName: null,
    price: null,
    collectionPoints: new List(),
    suggestedCollectionPoint: null,
    isTrackable: null,
    isSignedFor: null,
    isPrintedEticket: null,
    isDeliverableOnTime: false,
    isEnforced: false,
    errors: null,
    promotionalDiscountDelivery: null
};

const DeliveryOptionOrder = {
    [FulfilmentType.S_TICKET]: 1,
    [FulfilmentType.SMARTCARD]: 2,
    [FulfilmentType.E_TICKET]: 3,
    [FulfilmentType.PRINT_AT_HOME]: 4,
    [FulfilmentType.COLLECTION]: 5,
    [FulfilmentType.POSTAL]: 6,
};

const SouthEasternDeliveryOptionOrder = {
    ...DeliveryOptionOrder,
    [FulfilmentType.SMARTCARD]: 3,
    [FulfilmentType.E_TICKET]: 2,
};

class DeliveryOption extends record(defaults) {
    static fromJSON(json = {}) {
        const filterCantDeliver = error =>
            Object.keys(error)[0] === SmartcardErrorBlocking.CANT_DELIVER_IN_TIME;
        const filterEverythingElse = error =>
            Object.keys(error)[0] !== SmartcardErrorBlocking.CANT_DELIVER_IN_TIME;

        // Move the "Can't deliver" error to the top of the list
        const reorderedErrors =
            json.errors && json.errors.length
                ? [
                      ...json.errors.filter(filterCantDeliver),
                      ...json.errors.filter(filterEverythingElse),
                  ]
                : json.errors;

        return construct(DeliveryOption, json, {
            id: fields.string,
            fulfilmentMethod: fields.string,
            internalName: fields.string,
            displayName: fields.string,
            price: fields.number,
            suggestedCollectionPoint: resolve.as(Station),
            collectionPoints: resolveAll.as(Station),
            isTrackable: fields.bool,
            isSignedFor: fields.bool,
            isPrintedEticket: fields.bool,
            isDeliverableOnTime: fields.bool,
            promotionalDiscountDelivery: resolve.with(Object),
            isEnforced: fields.bool,

            // This is an array of objects each with a single key which is the error code,
            // and the value is an empty object. This leads to some quite opaque and confusing code.
            // Might be better to refactor this to a simple array of error codes
            errors: resolve(reorderedErrors).with(Object),
        });
    }

    getOrder() {
        // Brand is read here, not at module load, because config is not initialised yet then
        const order = config.get('brand') === 'southeastern'
            ? SouthEasternDeliveryOptionOrder
            : DeliveryOptionOrder;
        return order[this.fulfilmentMethod];
    }

    getFirstAvailableDeliveryOption(delivery, deliveryOptions) {
        const isUserLoggedIn = UserStore.getCurrent().isLoggedIn();

        const SmartCardBlockingAdditional = isUserLoggedIn ? SmartCardErrorBlockingForLoggedIn : {};

        const isSmartcard = delivery.fulfilmentMethod === FulfilmentType.SMARTCARD;
        const isSTicket = delivery.fulfilmentMethod === FulfilmentType.S_TICKET;

        const shouldSelectSTicket = FeatureFlagStore.get('enableSTicketsDelivery') && FeatureFlagStore.get('enableSTickets') && !this.errors;

        if (
            delivery.fulfilmentMethod &&
            deliveryOptions.find(option => option.fulfilmentMethod === delivery.fulfilmentMethod) &&
            !isSmartcard &&
            !isSTicket
        ) {
            return this.fulfilmentMethod === delivery.fulfilmentMethod;
        }

        if (this.fulfilmentMethod === FulfilmentType.S_TICKET) {
            return shouldSelectSTicket;
        }

        if (FeatureFlagStore.get('enableSmartcardMaintenance') || !FeatureFlagStore.get('enableSmartcard')) {
            return this.fulfilmentMethod !== FulfilmentType.SMARTCARD;
        }

        return (
            !this.errors ||
            !JSON.stringify(this.errors).match(
                new RegExp(
                    Object.values({
                        ...SmartcardErrorBlocking,
                        ...SmartcardServiceErrors,
                        ...SmartCardBlockingAdditional,
                    }).join('|'),
                    'g',
                ),
            )
        );
    }

    isValidWithPromo() {
        if (!this.promotionalDiscountDelivery) {
            return true;
        }

        return this.promotionalDiscountDelivery.isApplicable;
    }

    getPrice() {
        let price = this.price;

        if (!this.isValidWithPromo()) {
            price += this.promotionalDiscountDelivery.value;
        }

        return price;
    }

    getValidAssociatedSmartcards = ({ fare, associatedSmartcards }) => {
        if (!FeatureFlagStore.get('enableChildSmartcardTransitioning')) {
            return associatedSmartcards;
        }

        return associatedSmartcards.filter(card => {
            const fareValidTo = fare.getValidTo();

            if (card.isAdult) {
                return true;
            } else {
                // CORE-19703
                // If user has initiated a transfer process then allow this expiring child card.
                // During the transfer process the products are going to be placed in the new child smartcard.
                if (card.isNewCardRequestConfirmed) {
                    return true;
                }
            }

            if (fareValidTo.isAfter(card.getExpiryDateMoment())) {
                return false;
            }

            const cardHolderDateOfBirthMoment = card.getCardHolderDateOfBirthMoment();

            if (fare.isSeason()) {
                return fareValidTo.diff(cardHolderDateOfBirthMoment, 'months') < 16 * 12 + 4;
            } else {
                return fareValidTo.diff(cardHolderDateOfBirthMoment, 'years') < 16;
            }
        });
    };

    shouldHideSmartcard(smartcard, associatedSmartcards, allowMixedPreencodeAndLinkedFulfilment, includesChildPreencode) {
        if (includesChildPreencode) {
            return true;
        }

        const isUserLoggedIn = UserStore.getCurrent().isLoggedIn();

        const SmartCardBlockingAdditional = isUserLoggedIn ? SmartCardErrorBlockingForLoggedIn : {};

        const errors = this.get('errors');

        let updatedErrors = errors || [];

        /*
         Note, we have to do this as sometimes the deliver options
         endpoint returns a 'SmartcardCantBeDeliveredInTimeError' error,
         but sometimes not, and just 'isDeliverableOnTime: false' instead.
        */
        if (
            isUserLoggedIn &&
            this.isDeliverableOnTime === false &&
            !smartcard && associatedSmartcards.size < 1
        ) {
            updatedErrors[1] = {
                SmartcardCantBeDeliveredInTimeError: {},
            };
        }

        // Override for when we have the 'allowMixedPreencodeAndLinkedFulfilment' scenario
        const filteredDeliveryErrors =
            updatedErrors &&
            updatedErrors.filter(e =>
                allowMixedPreencodeAndLinkedFulfilment
                    ? !(SmartCardErrorBlockingForLoggedIn.NOT_ENOUGH_ACTIVE_SMARTCARDS_ERROR in e)
                    : () => true,
            );

        const deliveryErrors = JSON.stringify(filteredDeliveryErrors) || '';

        return Object.values({ ...SmartcardErrorBlocking, ...SmartCardBlockingAdditional }).some(
            e => deliveryErrors.indexOf(e) > 0,
        );
    }

    requiresLocation() {
        return this.isCollection() || this.isSmartcard();
    }

    isPrintAtHome() {
        return this.fulfilmentMethod === FulfilmentType.PRINT_AT_HOME;
    }

    isETicket() {
        return this.fulfilmentMethod === FulfilmentType.E_TICKET;
    }

    isCollection() {
        return this.fulfilmentMethod === FulfilmentType.COLLECTION;
    }

    isPostalDelivery() {
        return this.fulfilmentMethod === FulfilmentType.POSTAL;
    }

    isETicketByPost() {
        return this.fulfilmentMethod === FulfilmentType.POSTAL && this.isPrintedEticket;
    }

    isSmartcard() {
        return this.fulfilmentMethod === FulfilmentType.SMARTCARD;
    }

    isSTicket() {
        return this.fulfilmentMethod === FulfilmentType.S_TICKET;
    }

    hasError(error) {
        return this.getErrors().some(deliveryError => deliveryError === error);
    }

    hasDeliveryErrors() {
        return this.errors && this.errors.length > 0;
    }

    getErrors() {
        return this.hasDeliveryErrors() ? this.errors.map(error => Object.keys(error)[0]) : [];
    }

    getEarliestDeliveryDate() {
        const days = this.isSmartcard()
            ? config.get('minAwaitingSmartcardProductDays')
            : config.get('minDeliveryLeadTimeInDays');
        let earliest = trainTime().addWorkingDays(days);

        if (trainTime().hours() >= config.get('pivotalPostalHour')) {
            // If it's after last post, then make it the next day
            earliest = earliest.addWorkingDays(1);
        }
        return earliest;
    }

    hasSmartcardBlockingErrors() {
        if (this.errors) {
            // convert errors and known error constants to same format for comparison
            const smartcardErrorBlockingStrings = Object.values({
                ...SmartcardErrorBlocking,
                ...SmartcardServiceErrors,
            }).map(x => x.toLowerCase());

            // convert errors and known error constants to same format for comparison
            const errorStrings = this.errors
                .map(err => {
                    return Object.keys(err).map(x => x.toLowerCase());
                })
                .flat();

            // check if fulfilment errors are 'blocking' errors
            return errorStrings.some(errString =>
                smartcardErrorBlockingStrings.includes(errString),
            );
        } else {
            return false;
        }
    }
}

export default DeliveryOption;
